import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { all_routes } from "../../router/all_routes"
import TooltipOption from "../../../core/common/tooltipOption"
import Table from "../../../core/common/dataTable/index"
import type { ColumnsType } from "antd/es/table"
import ImageWithBasePath from "../../../core/common/imageWithBasePath"
import { getStudentMonthlyReport } from "../../../service/reports"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { toast } from "react-toastify"
import CommonSelect from "../../../core/common/commonSelect"
import { getAllClasses, type Class } from "../../../service/classes"
import { Option } from "../../../core/common/multiSelect"
import { useSelector } from "react-redux"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { isTeacher } from "../../../utils/rbacHelper"
import { selectUser } from "../../../core/data/redux/slice/user"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { absoluteCampusLogoUrl, campusBrandName } from "../../../utils/campusLogos"
import { code39SvgString } from "../../../core/common/barcode/code39"

const STATUS_COLORS: Record<string, string> = {
  present: "#10B981",
  absent: "#EF4444",
  late: "#3d5ee1",
  "half-day": "#A855F7",
  leave: "#3B82F6",
  suspended: "#F59E0B",
  struck_off: "#DC2626",
  "off-day": "#64748B",
  "not-marked": "#9CA3AF",
}

const STATUS_LABEL: Record<string, string> = {
  present: "Present",
  absent: "Absent",
  late: "Late",
  "half-day": "Half Day",
  leave: "Leave",
  suspended: "Suspended",
  struck_off: "Struck Off",
  "off-day": "Off Day",
  "not-marked": "Not Marked",
}

// Server-side sort — sortBy values map to fields the backend understands
// (see routes/attendance.js student-monthly-report: "name" -> user.firstName,
// "grNumber" -> student.grNumber).
const STUDENT_SORT_OPTIONS = [
  { label: "GR Number", value: { sortBy: "grNumber", sortOrder: "asc" } },
  { label: "Name (A-Z)", value: { sortBy: "name", sortOrder: "asc" } },
  { label: "Name (Z-A)", value: { sortBy: "name", sortOrder: "desc" } },
]

// No pagination on this screen — fetch the whole filtered roster in one call.
const REPORT_ROW_LIMIT = 100000

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

// Type definitions
interface DayDetail {
  status?: string
  checkIn?: string
  checkOut?: string
}

interface TableRowData {
  key: string
  originalData: any
  dayDetails: Record<number, DayDetail>
  user: any
  student: any
  percentage: number
  percentageDisplay: string
  // School days for this campus this month -- the denominator behind %.
  // Weekends, holidays and School Opening CLOSED days are excluded from it,
  // so it is not simply the number of days in the month.
  w: number
  p: number
  l: number
  a: number
  h: number
  lv: number
  sp: number
  st: number
  img: string
  percentClass: string
  [key: string]: any
}

const AttendanceReport = () => {
  const routes = all_routes
  const user = useSelector(selectUser)
  const currentBranch = useSelector(selectCurrentBranch)
  const isUserTeacher = isTeacher(user)
  const currentSession = useSelector(selectCurrentSession)
  const systems = useSelector(selectSystemsForCurrentBranch)

  const [tableData, setTableData] = useState<TableRowData[]>([])

  // Get current month in Asia/Karachi timezone
  const getCurrentKarachiMonth = (): string => {
    const now = new Date()
    const karachiDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Karachi" }),
    )
    const year = karachiDate.getFullYear()
    const month = String(karachiDate.getMonth() + 1).padStart(2, "0")
    return `${year}-${month}`
  }

  // "Applied" — only committed (and only then fetched) when Search is
  // clicked, so the report shows nothing until the user explicitly asks for it.
  const [selectedMonth, setSelectedMonth] = useState<string>(
    getCurrentKarachiMonth(),
  )
  const [loading, setLoading] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [sortBy, setSortBy] = useState(STUDENT_SORT_OPTIONS[0])
  const [classesData, setClassesData] = useState<Class[]>([])
  const [selectedClassName, setSelectedClassName] = useState("")
  const [selectedSectionName, setSelectedSectionName] = useState("")
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [hasSearched, setHasSearched] = useState(false)

  const [appliedFilters, setAppliedFilters] = useState({
    classId: "",
    sectionId: "",
    system: systems[0]?._id,
    sortBy: STUDENT_SORT_OPTIONS[0].value.sortBy,
    sortOrder: STUDENT_SORT_OPTIONS[0].value.sortOrder as "asc" | "desc",
  })

  // "Draft" — bound to the filter controls directly; only copied into
  // appliedFilters (and thus fetched) on Search.
  const [monthDraft, setMonthDraft] = useState<string>(getCurrentKarachiMonth())
  const [draftSystem, setDraftSystem] = useState(systems[0]?._id)
  const [draftClassId, setDraftClassId] = useState("")
  const [draftSectionId, setDraftSectionId] = useState("")
  const [draftClassName, setDraftClassName] = useState("")
  const [draftSectionName, setDraftSectionName] = useState("")
  const [classOptions, setClassOptions] = useState<Option[]>([])
  const [sectionOptions, setSectionOptions] = useState<Option[]>([])

  const selectedDate = new Date(selectedMonth)
  const currentMonth = selectedDate.getMonth() + 1
  const currentYear = selectedDate.getFullYear()

  // Helper function to get current date in Asia/Karachi as YYYY-MM-DD string
  const getTodayStr = (): string => {
    const now = new Date()
    const karachiDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Karachi" }),
    )
    const year = karachiDate.getFullYear()
    const month = String(karachiDate.getMonth() + 1).padStart(2, "0")
    const day = String(karachiDate.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const getPercentageClass = (percentage: number): string => {
    if (percentage >= 90) return "text-success"
    if (percentage >= 75) return "text-warning"
    return "text-danger"
  }

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case "present":
        return "1"
      case "late":
        return "2"
      case "absent":
        return "3"
      case "half-day":
        return "4"
      case "leave":
        return "6"
      case "off-day":
        return "7"
      case "suspended":
        return "8"
      case "struck_off":
        return "9"
      default:
        return "5"
    }
  }

  const getTagBackgroundColor = (status: string): string =>
    STATUS_COLORS[status?.toLowerCase()] ?? STATUS_COLORS["not-marked"]

  const getTagTextColor = (status: string): string => {
    const s = status?.toLowerCase()
    return s === "late" || s === "not-marked" || s === "" ? "#1a1a1a" : "#fff"
  }

  const normalizeStatus = (rawStatus: any): string => {
    const s = String(rawStatus ?? "")
      .trim()
      .toLowerCase()

    if (s === "1") return "present"
    if (s === "2") return "late"
    if (s === "3") return "absent"
    if (s === "4") return "half-day"
    if (s === "6") return "leave"
    if (s === "7") return "off-day"
    if (s === "8") return "suspended"
    if (s === "9") return "struck_off"

    if (s === "half day" || s === "halfday") return "half-day"
    if (s === "off day" || s === "offday" || s === "sunday") return "off-day"
    if (s === "not marked" || s === "notmarked" || s === "unmarked")
      return "not-marked"

    if (s === "") return "not-marked"

    return s
  }

  const formatTime = (value: string | undefined): string => {
    if (!value || typeof value !== "string") return ""
    const trimmed = value.trim()
    if (!trimmed) return ""
    const date = new Date(trimmed)
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-US", {
        timeZone: "Asia/Karachi",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
    }
    const timeMatch = trimmed.match(/(\d{2}:\d{2}:\d{2})/)
    return timeMatch ? timeMatch[1] : trimmed
  }

  const fetchClasses = async () => {
    try {
      setLoadingClasses(true)
      const classesResponse = await getAllClasses({
        limit: 100,
        system: draftSystem,
        // Without this, class.sections returns every session's sections ever
        // created for this class (never pruned), showing duplicate "A"/"B"/...
        // entries in the Section filter once a class has existed 2+ years.
        session: currentSession?._id,
      })
      const classes = classesResponse.classes
      setClassesData(classes)
      const classOpts: Option[] = classes.map((cls: Class) => ({
        label: cls.name,
        value: cls._id,
      }))
      setClassOptions(classOpts)
    } catch (error) {
      console.error("Error fetching classes:", error)
      toast.error("Failed to fetch classes")
    } finally {
      setLoadingClasses(false)
    }
  }

  const transformStudentDataToTableFormat = (
    apiData: any[],
  ): TableRowData[] => {
    if (!apiData || !Array.isArray(apiData)) return []

    const todayStr = getTodayStr()
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()

    return apiData.map(student => {
      // Create a map from attendanceRecords (MORE RELIABLE than dailyBreakdown)
      const attendanceRecords = student.attendanceRecords || []
      const recordsByDay = new Map()

      attendanceRecords.forEach((record: any) => {
        const date = new Date(record.date)
        const dayNum = date.getDate()
        recordsByDay.set(dayNum, record)
      })

      const summary = student.summary || {}

      const tableRow: TableRowData = {
        key:
          student.student?._id || student.user?._id || Math.random().toString(),
        originalData: student,
        dayDetails: {},
        user: student.user,
        student: student.student,
        percentage: summary.attendancePercentage || 0,
        percentageDisplay: `${summary.attendancePercentage || 0}%`,
        w: summary.workingDays || 0,
        p:
          (summary.presentDays || 0) +
          (summary.lateDays || 0) +
          (summary.halfDays || 0),
        l: summary.lateDays || 0,
        a: 0,
        h: summary.halfDays || 0,
        lv: summary.leaveDays || 0,
        sp: summary.suspendedDays || 0,
        st: summary.struckOffDays || 0,
        img:
          student.user?.profilePicture || "/assets/img/profiles/avatar-01.jpg",
        percentClass: getPercentageClass(summary.attendancePercentage || 0),
      }

      let computedAbsent = 0

      for (let day = 1; day <= daysInMonth; day++) {
        const dayKey = `d${day.toString().padStart(2, "0")}`
        const dayDate = new Date(currentYear, currentMonth - 1, day)
        const dayDateStr = dayDate.toISOString().split("T")[0]
        const isSunday = dayDate.getDay() === 0
        const isFutureDate = dayDateStr > todayStr

        if (isFutureDate) {
          tableRow[dayKey] = isSunday ? "7" : "5"
          continue
        }

        // PRIMARY: Get from attendanceRecords (most reliable)
        const dayRecord = recordsByDay.get(day)

        if (dayRecord) {
          let derivedCheckIn: string | undefined
          let derivedCheckOut: string | undefined

          if (dayRecord.inOut && Array.isArray(dayRecord.inOut)) {
            const punches = dayRecord.inOut.slice().reverse()
            const latestIn = punches.find((r: any) => r.type === "in")
            const latestOut = punches.find((r: any) => r.type === "out")
            if (latestIn?.time) derivedCheckIn = latestIn.time
            if (latestOut?.time) derivedCheckOut = latestOut.time
          }

          tableRow.dayDetails[day] = {
            status: dayRecord.status,
            checkIn: derivedCheckIn,
            checkOut: derivedCheckOut,
          }

          const normalized = normalizeStatus(dayRecord.status)
          tableRow[dayKey] = getStatusColor(normalized)
        } else {
          // Fallback to dailyBreakdown if no attendance record
          const dailyBreakdown = student.dailyBreakdown || {}
          const breakdownStatus = dailyBreakdown[day]?.status

          if (breakdownStatus) {
            const normalized = normalizeStatus(breakdownStatus)
            tableRow[dayKey] = getStatusColor(normalized)
          } else {
            tableRow[dayKey] = isSunday ? "7" : "5"
          }
        }

        if (tableRow[dayKey] === "3") computedAbsent += 1
      }

      tableRow.a = computedAbsent
      return tableRow
    })
  }

  const fetchStudentReport = async () => {
    try {
      setLoading(true)

      const selectedDateObj = new Date(selectedMonth)
      const monthNumber = selectedDateObj.getMonth() + 1
      const yearNumber = selectedDateObj.getFullYear()

      const response = await getStudentMonthlyReport({
        month: monthNumber,
        year: yearNumber,
        classId: appliedFilters.classId || undefined,
        sectionId: appliedFilters.sectionId || undefined,
        systemId: appliedFilters.system || undefined,
        sessionId: currentSession?._id || undefined,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: 1,
        limit: REPORT_ROW_LIMIT,
        searchText,
      })

      if (response.success) {
        const transformedData = transformStudentDataToTableFormat(response.data)
        setTableData(transformedData)
      } else {
        setTableData([])
      }
    } catch (error) {
      console.error("Error fetching student report:", error)
      setTableData([])
    } finally {
      setLoading(false)
    }
  }

  const handleMonthChange = (month: string) => {
    setMonthDraft(month)
  }

  const handleSortSelect = (item: {
    label: string
    value: {
      sortBy: string
      sortOrder: string
    }
  }) => {
    setSortBy(item)
    setAppliedFilters(prev => ({
      ...prev,
      sortBy: item.value.sortBy,
      sortOrder: item.value.sortOrder as "asc" | "desc",
    }))
  }

  const handleSystemChange = (systemItem: any) => {
    setDraftSystem(systemItem?._id)
    setDraftClassId("")
    setDraftSectionId("")
    setDraftClassName("")
    setDraftSectionName("")
    setSectionOptions([])
  }

  const handleClassFilterChange = (selectedOption: any) => {
    const cls = classesData.find(c => c._id === selectedOption?.value)
    setDraftClassId(selectedOption?.value || "")
    setDraftClassName(selectedOption?.label || "")
    setDraftSectionId("")
    setDraftSectionName("")
    setSectionOptions(
      cls?.sections.map((s: { _id: string; name: string }) => ({
        label: s.name,
        value: s._id,
      })) || [],
    )
  }

  const handleSectionFilterChange = (selectedOption: any) => {
    setDraftSectionId(selectedOption?.value || "")
    setDraftSectionName(selectedOption?.label || "")
  }

  const handleSearchClick = () => {
    setSelectedMonth(monthDraft)
    setSelectedClassName(draftClassName)
    setSelectedSectionName(draftSectionName)
    setAppliedFilters(prev => ({
      ...prev,
      system: draftSystem,
      classId: draftClassId,
      sectionId: draftSectionId,
    }))
    setHasSearched(true)
  }

  const handleResetFilters = () => {
    const defaultMonth = getCurrentKarachiMonth()
    const defaultSystem = systems[0]?._id

    setMonthDraft(defaultMonth)
    setDraftSystem(defaultSystem)
    setDraftClassId("")
    setDraftSectionId("")
    setDraftClassName("")
    setDraftSectionName("")
    setSectionOptions([])
    setSortBy(STUDENT_SORT_OPTIONS[0])
    setSearchText("")

    setSelectedMonth(defaultMonth)
    setSelectedClassName("")
    setSelectedSectionName("")
    setAppliedFilters({
      classId: "",
      sectionId: "",
      system: defaultSystem,
      sortBy: STUDENT_SORT_OPTIONS[0].value.sortBy,
      sortOrder: STUDENT_SORT_OPTIONS[0].value.sortOrder as "asc" | "desc",
    })

    setHasSearched(false)
    setTableData([])
  }

  const handleRefresh = async () => {
    if (!hasSearched) return
    await fetchStudentReport()
  }

  const buildPrintHeaders = () => {
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    // Barcode is deliberately not a column here — it only belongs on the
    // blank sheet (handlePrintBlank), which exists to be scanned; this print
    // is of an already-filled report, with nothing left to scan against.
    const summaryHeaders = ["S#", "Student Name", "GR No", "%", "P", "L", "A", "H", "Lv"]
    const dayHeaders: string[] = []
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentYear, currentMonth - 1, day)
      dayHeaders.push(`${day.toString().padStart(2, "0")}<br>${dayNames[dayDate.getDay()]}`)
    }
    return { summaryHeaders, dayHeaders, daysInMonth }
  }

  const buildPrintRows = (allRows: TableRowData[], daysInMonth: number) => {
    const statusText: Record<string, string> = {
      "1": "P",
      "2": "L",
      "3": "A",
      "4": "H",
      "5": "",
      "6": "Lv",
      "7": "OFF",
      "8": "S",
      "9": "ST",
    }
    return allRows.map((item, idx) => {
      const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim()
      const nameHtml = fullName.split(" ").filter(Boolean).join("<br>")
      const gr = String(item.student?.grNumber || "").trim()
      const summary = [
        idx + 1,
        nameHtml,
        gr,
        item.percentage,
        item.p,
        item.l,
        item.a,
        item.h,
        item.lv,
      ]
      const days: string[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        const dayKey = `d${day.toString().padStart(2, "0")}`
        const code = String(item[dayKey] ?? "5")
        const status = statusText[code] ?? ""
        const detail = item.dayDetails?.[day]
        const inTime = formatTime(detail?.checkIn)
        const outTime = formatTime(detail?.checkOut)
        if (inTime || outTime) {
          const t1 = inTime ? inTime.substring(0, 5).replace(":", "") : ""
          const t2 = outTime ? outTime.substring(0, 5).replace(":", "") : ""
          days.push(`${status}<br>${t1}<br>${t2}`)
        } else {
          days.push(status)
        }
      }
      return [...summary, ...days]
    })
  }

  const printCSS = `
    body { font-family: Arial, sans-serif; font-size: 7px; margin: 0; padding: 0; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 1px 2px; text-align: center; vertical-align: middle; }
    th { background: #2980b9; color: #fff; white-space: nowrap; font-size: 7px; }
    td { font-size: 7px; }
    .page-break { page-break-after: always; }
    @page { margin: 3mm; }
    .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; border-bottom: 1.5px solid #2980b9; padding-bottom: 3px; }
    .school-brand { display: flex; align-items: center; gap: 5px; }
    .school-brand img { height: 34px; object-fit: contain; }
    .school-brand strong { font-size: 13px; letter-spacing: 1px; }
    .report-meta { text-align: center; font-size: 8px; line-height: 1.5; }
    .sheet-info { text-align: right; font-size: 8px; line-height: 1.5; }
  `

  const handlePrintClick = async () => {
    if (!tableData.length) {
      toast.warning("No data to print")
      return
    }
    setPrintLoading(true)
    try {
      const allRows = tableData
      const { summaryHeaders, dayHeaders, daysInMonth } = buildPrintHeaders()
      const headerRow = [...summaryHeaders, ...dayHeaders]
      const bodyRows = buildPrintRows(allRows, daysInMonth)

      const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)
      const brandName = escapeHtml(campusBrandName(currentBranch))
      const headerHtml = `
        <div class="print-header">
          <div class="school-brand">
            <img src="${logoUrl}" onerror="this.style.display='none'" />
            <strong>${brandName}</strong>
          </div>
          <div class="report-meta">
            Class: ${selectedClassName || "All Classes"} &nbsp;|&nbsp; Section: ${selectedSectionName || "All Sections"}
          </div>
          <div class="sheet-info">
            Attendance Sheet ${selectedMonth}
          </div>
        </div>
      `

      const PAGE_SIZE = 25
      const theadHtml = `<thead><tr>${headerRow.map(h => `<th>${h}</th>`).join("")}</tr></thead>`
      const pages: string[] = []
      for (let i = 0; i < bodyRows.length; i += PAGE_SIZE) {
        const chunk = bodyRows.slice(i, i + PAGE_SIZE)
        pages.push(`<table>${theadHtml}<tbody>${chunk.map(r => `<tr>${r.map(c => `<td>${c ?? ""}</td>`).join("")}</tr>`).join("")}</tbody></table>`)
      }

      const html = `<html><head><title>Student Attendance Report</title>
        <meta charset="utf-8"/>
        <style>${printCSS}
          td svg { height: 22px; width: auto; max-width: 150px; }
        </style></head>
        <body>
          ${headerHtml}
          ${pages.join('<div class="page-break"></div>')}
        </body></html>`

      const win = window.open("", "_blank")
      if (!win) {
        toast.error("Popup blocked — allow popups and try again")
        return
      }
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print()
        win.close()
      }, 250)
    } catch (e) {
      console.error(e)
      toast.error("Failed to load data for print")
    } finally {
      setPrintLoading(false)
    }
  }

  // Blank attendance sheet with a scannable barcode per student. Lists the FULL
  // roster (not the current page), columns: S# | GR | Student Name | Barcode |
  // day columns (blank to mark; Sundays pre-marked "Sun"). Barcode = Code 39 of
  // the GR so the same sheet can be scanned on the Student Attendance page.
  const handlePrintBlank = async () => {
    if (!tableData.length) {
      toast.warning("No data to print")
      return
    }
    setPrintLoading(true)
    try {
      const allRows = tableData
      const { dayHeaders, daysInMonth } = buildPrintHeaders()
      const headerRow = ["S#", "GR", "Student Name", "Barcode", ...dayHeaders]

      // Sundays are pre-marked "Sun" (off day), like the reference sheet.
      const isSunday: boolean[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        isSunday[day] = new Date(currentYear, currentMonth - 1, day).getDay() === 0
      }

      const bodyRows = allRows
        .map((item, idx) => {
          const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim()
          const gr = String(item.student?.grNumber || "").trim()
          const barcode = gr ? code39SvgString(gr, { height: 34, unit: 1, showText: false }) : ""
          const dayCells = []
          for (let day = 1; day <= daysInMonth; day++) {
            dayCells.push(`<td>${isSunday[day] ? "Sun" : "&nbsp;"}</td>`)
          }
          return `<tr><td>${idx + 1}</td><td>${escapeHtml(gr)}</td><td class="name-cell">${escapeHtml(fullName)}</td><td class="barcode-cell">${barcode}</td>${dayCells.join("")}</tr>`
        })
        .join("")

      const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl)
      const brandName = escapeHtml(campusBrandName(currentBranch))
      const headerHtml = `
        <div class="print-header">
          <div class="school-brand">
            <img src="${logoUrl}" onerror="this.style.display='none'" />
            <strong>${brandName}</strong>
          </div>
          <div class="report-meta">
            <div>Class: ${selectedClassName || "All Classes"}</div>
            <div>Section: ${selectedSectionName || "All Sections"}</div>
          </div>
          <div class="sheet-info">
            <div>Attendance Sheet</div>
            <div>${selectedMonth}</div>
          </div>
        </div>
      `

      const html = `<html><head><title>Blank Attendance Sheet</title>
        <style>${printCSS}
          td { min-height: 14px; }
          td.name-cell { white-space: nowrap; text-align: left; }
          td.barcode-cell { text-align: center; }
          td.barcode-cell svg { height: 30px; width: auto; max-width: 170px; }
        </style></head>
        <body>
          ${headerHtml}
          <table>
            <thead><tr>${headerRow.map(h => `<th>${h}</th>`).join("")}</tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </body></html>`

      const win = window.open("", "_blank")
      if (!win) {
        toast.error("Popup blocked — allow popups and try again")
        return
      }
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print()
        win.close()
      }, 300)
    } catch (error) {
      console.error("Print blank error:", error)
      toast.warning("Failed to build the blank sheet")
    } finally {
      setPrintLoading(false)
    }
  }

  const handleExportPdf = async () => {
    if (!tableData.length) {
      toast.warning("No data to export")
      return
    }
    setPdfLoading(true)
    try {
      const selectedDateObj = new Date(selectedMonth)
      const monthNumber = selectedDateObj.getMonth() + 1
      const yearNumber = selectedDateObj.getFullYear()
      const allRows = tableData
      const daysInMonth = new Date(yearNumber, monthNumber, 0).getDate()
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
      const statusText: Record<string, string> = {
        "1": "P",
        "2": "L",
        "3": "A",
        "4": "H",
        "5": "",
        "6": "Lv",
        "7": "OFF",
        "8": "S",
        "9": "ST",
      }

      const summaryHeaders = ["S#", "Student Name", "GR No", "%", "P", "L", "A", "H", "Lv"]
      const dayHeaders: string[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(yearNumber, monthNumber - 1, day)
        dayHeaders.push(
          `${day.toString().padStart(2, "0")} ${dayNames[dayDate.getDay()]}`,
        )
      }
      const head = [[...summaryHeaders, ...dayHeaders]]

      const body = allRows.map((item, idx) => {
        const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim()
        const summary = [
          idx + 1,
          fullName,
          item.student?.grNumber || "",
          `${item.percentage}%`,
          item.p,
          item.l,
          item.a,
          item.h,
          item.lv,
        ]
        const days: string[] = []
        for (let day = 1; day <= daysInMonth; day++) {
          const dayKey = `d${day.toString().padStart(2, "0")}`
          const code = String(item[dayKey] ?? "5")
          const status = statusText[code] ?? ""
          const detail = item.dayDetails?.[day]
          const inTime = formatTime(detail?.checkIn)
          const outTime = formatTime(detail?.checkOut)
          if (inTime || outTime) {
            const t1 = inTime ? inTime.substring(0, 5).replace(":", "") : ""
            const t2 = outTime ? outTime.substring(0, 5).replace(":", "") : ""
            days.push(`${status} ${t1 && t2 ? `${t1}-${t2}` : t1 || t2}`.trim())
          } else {
            days.push(status)
          }
        }
        return [...summary, ...days]
      })

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })
      doc.setFontSize(10)
      doc.text(`Student Attendance Report — ${selectedMonth}`, 14, 10)
      autoTable(doc, {
        head,
        body,
        startY: 15,
        styles: { fontSize: 4, cellPadding: 1, overflow: "linebreak" },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 5 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 8 } },
      })
      const monthStr = selectedMonth.substring(0, 7)
      doc.save(`student_attendance_report_${monthStr}.pdf`)
      toast.success("Exported to PDF successfully")
    } catch (error) {
      console.error("Error exporting to PDF:", error)
      toast.error("Failed to export to PDF")
    } finally {
      setPdfLoading(false)
    }
  }

  const handleExportExcel = async () => {
    if (!tableData.length) {
      toast.warning("No data to export")
      return
    }
    setExcelLoading(true)
    try {
      const selectedDateObj = new Date(selectedMonth)
      const monthNumber = selectedDateObj.getMonth() + 1
      const yearNumber = selectedDateObj.getFullYear()
      const allRows = tableData
      const daysInMonth = new Date(yearNumber, monthNumber, 0).getDate()
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

      const statusText: Record<string, string> = {
        "1": "P",
        "2": "L",
        "3": "A",
        "4": "H",
        "5": "-",
        "6": "Lv",
        "7": "OFF",
        "8": "S",
        "9": "ST",
      }

      const rows = allRows.map(item => {
        const row: Record<string, any> = {
          "Student Name":
            `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim(),
          "GR No": item.student?.grNumber || "N/A",
          Class: item.originalData?.class?.name || "N/A",
          Section: item.originalData?.section?.name || "N/A",
          "%": `${item.percentage}%`,
          P: item.p,
          L: item.l,
          A: item.a,
          H: item.h,
          Lv: item.lv,
        }
        for (let day = 1; day <= daysInMonth; day++) {
          const dayDate = new Date(yearNumber, monthNumber - 1, day)
          const dayLabel = `${day.toString().padStart(2, "0")} (${dayNames[dayDate.getDay()]})`
          const dayKey = `d${day.toString().padStart(2, "0")}`
          const code = String(item[dayKey] ?? "5")
          const status = statusText[code] ?? "-"
          const detail = item.dayDetails?.[day]
          const inTime = formatTime(detail?.checkIn)
          const outTime = formatTime(detail?.checkOut)
          if (inTime || outTime) {
            const t1 = inTime ? inTime.substring(0, 5) : ""
            const t2 = outTime ? outTime.substring(0, 5) : ""
            const timeStr = t1 && t2 ? `${t1} - ${t2}` : t1 || t2
            row[dayLabel] = `${status} (${timeStr})`
          } else {
            row[dayLabel] = status
          }
        }
        return row
      })

      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report")
      const monthStr = selectedMonth.substring(0, 7) // YYYY-MM
      XLSX.writeFile(workbook, `student_attendance_report_${monthStr}.xlsx`)
      toast.success("Exported to Excel successfully")
    } catch (error) {
      console.error("Error exporting to Excel:", error)
      toast.error("Failed to export to Excel")
    } finally {
      setExcelLoading(false)
    }
  }

  useEffect(() => {
    fetchClasses()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSystem, currentSession?._id])

  // Only fetch once the user has clicked Search — nothing loads on mount.
  useEffect(() => {
    if (!hasSearched) return
    fetchStudentReport()
  }, [hasSearched, appliedFilters, selectedMonth, currentSession?._id, searchText])

  const renderTitle = (title1: string, title2: string) => (
    <div className="text-center">
      <span className="day-num d-block">{title1}</span>
      <span>{title2}</span>
    </div>
  )

  const generateDayColumns = (): ColumnsType<TableRowData> => {
    const dayColumns: ColumnsType<TableRowData> = []
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
    const dayNames = ["S", "M", "T", "W", "T", "F", "S"]
    const todayStr = getTodayStr()

    for (let day = 1; day <= daysInMonth; day++) {
      const dayNum = day
      const date = new Date(currentYear, currentMonth - 1, day)
      const dayName = dayNames[date.getDay()]
      const isFutureDate = date.toISOString().split("T")[0] > todayStr

      dayColumns.push({
        title: () => renderTitle(day.toString().padStart(2, "0"), dayName),
        dataIndex: `d${day.toString().padStart(2, "0")}`,
        key: `day_${day}`,
        render: (text: string, record: TableRowData) => {
          if (isFutureDate) {
            return (
              <div className="d-flex flex-column align-items-center gap-1">
                <span
                  className="attendance-range"
                  style={{
                    backgroundColor:
                      date.getDay() === 0
                        ? STATUS_COLORS["off-day"]
                        : STATUS_COLORS["not-marked"],
                  }}
                  title={date.getDay() === 0 ? "Off Day" : "Not Marked"}
                />
              </div>
            )
          }

          const detail = record.dayDetails?.[dayNum]
          const inTime = formatTime(detail?.checkIn)
          const outTime = formatTime(detail?.checkOut)

          if (inTime || outTime) {
            const bgColor = getTagBackgroundColor(detail?.status || text)
            const textColor = getTagTextColor(detail?.status || text)
            return (
              <div className="d-flex flex-column align-items-center gap-1">
                {inTime && (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "12px",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                    }}
                  >
                    IN: {inTime}
                  </span>
                )}
                {outTime && (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "12px",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                    }}
                  >
                    OUT: {outTime}
                  </span>
                )}
              </div>
            )
          }

          const normalized = normalizeStatus(text)
          const dotColor =
            STATUS_COLORS[normalized] ?? STATUS_COLORS["not-marked"]
          const statusText = STATUS_LABEL[normalized] ?? "Not Marked"

          return (
            <div className="d-flex flex-column align-items-center gap-1">
              <span
                className="attendance-range"
                style={{ backgroundColor: dotColor }}
                title={statusText}
              />
            </div>
          )
        },
      })
    }
    return dayColumns
  }

  const columns: ColumnsType<TableRowData> = [
    {
      title: "Student",
      dataIndex: "originalData",
      key: "student",
      render: (originalData: any) => {
        const user = originalData?.user
        const studentName = user
          ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
          : "Unknown Student"

        return (
          <div className="d-flex align-items-center">
            <Link to="#" className="avatar avatar-md">
              <ImageWithBasePath
                src={"assets/img/profiles/avatar-01.jpg"}
                fullSrc={user?.profilePicture}
                className="img-fluid rounded-circle"
                alt="img"
              />
            </Link>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <Link
                  to={routes.studentAttendanceDetails.replace(
                    ":enrollmentId",
                    originalData?._id,
                  )}
                >
                  {studentName}
                </Link>
              </p>
              <small className="text-muted">
                {originalData?.student?.grNumber || "No GR No"}
              </small>
            </div>
          </div>
        )
      },
      sorter: (a: TableRowData, b: TableRowData) => {
        const nameA =
          `${a.originalData?.user?.firstName || ""} ${a.originalData?.user?.lastName || ""}`.toLowerCase()
        const nameB =
          `${b.originalData?.user?.firstName || ""} ${b.originalData?.user?.lastName || ""}`.toLowerCase()
        return nameA.localeCompare(nameB)
      },
      fixed: "left" as const,
      width: 200,
    },
    {
      title: "%",
      dataIndex: "percentageDisplay",
      key: "percentage",
      render: (text: string, record: TableRowData) => (
        <span className={record.percentClass}>{text}</span>
      ),
      sorter: (a: TableRowData, b: TableRowData) => a.percentage - b.percentage,
      width: 80,
    },
    {
      // School days -- the number the percentage is divided by.
      title: "W",
      dataIndex: "w",
      key: "w",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.w - b.w,
    },
    {
      title: "P",
      dataIndex: "p",
      key: "p",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.p - b.p,
    },
    {
      title: "L",
      dataIndex: "l",
      key: "l",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.l - b.l,
    },
    {
      title: "A",
      dataIndex: "a",
      key: "a",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.a - b.a,
    },
    {
      title: "H",
      dataIndex: "h",
      key: "h",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.h - b.h,
    },
    {
      title: "Lv",
      dataIndex: "lv",
      key: "lv",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.lv - b.lv,
      render: (lv: number) => <span className="text-info">{lv}</span>,
    },
    {
      title: "Sp",
      dataIndex: "sp",
      key: "sp",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.sp - b.sp,
      render: (sp: number) => <span style={{ color: STATUS_COLORS.suspended }}>{sp}</span>,
    },
    {
      title: "ST",
      dataIndex: "st",
      key: "st",
      width: 50,
      sorter: (a: TableRowData, b: TableRowData) => a.st - b.st,
      render: (st: number) => <span style={{ color: STATUS_COLORS.struck_off }}>{st}</span>,
    },
    ...generateDayColumns(),
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Student Attendance Report</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Report</Link>
                  </li>
                  <li className="breadcrumb-item active">Attendance Report</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={handleRefresh}
                onPrint={handlePrintClick}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                onPrintBlank={handlePrintBlank}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
            </div>
          </div>

          <div className="filter-wrapper">
            <div className="list-tab">
              <ul>
                <li>
                  <Link to={routes.attendanceReport} className="active">
                    Student Report
                  </Link>
                </li>
                <li>
                  <Link
                    to={routes.staffReport}
                    className={isUserTeacher ? "not-allowed" : ""}
                    onClick={e => {
                      if (isUserTeacher) {
                        e.preventDefault()
                        toast.warning(
                          "You don't have permission to access staff attendance",
                        )
                      }
                    }}
                  >
                    Staff Report
                    {isUserTeacher && (
                      <small className="ms-1">(No permission)</small>
                    )}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="attendance-types page-header justify-content-end">
            <ul className="attendance-type-list">
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.present }}
                >
                  <i className="ti ti-checks" />
                </span>{" "}
                Present
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.absent }}
                >
                  <i className="ti ti-x" />
                </span>{" "}
                Absent
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.late }}
                >
                  <i className="ti ti-clock-x" />
                </span>{" "}
                Late
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS["half-day"] }}
                >
                  <i className="ti ti-calendar-event" />
                </span>{" "}
                Half Day
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.leave }}
                >
                  <i className="ti ti-calendar" />
                </span>{" "}
                Leave
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.suspended }}
                >
                  <i className="ti ti-ban" />
                </span>{" "}
                Suspended
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS.struck_off }}
                >
                  <i className="ti ti-gavel" />
                </span>{" "}
                Struck Off
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS["off-day"] }}
                >
                  <i className="ti ti-calendar-off" />
                </span>{" "}
                Off Day
              </li>
              <li>
                <span
                  className="attendance-icon"
                  style={{ backgroundColor: STATUS_COLORS["not-marked"] }}
                >
                  <i className="ti ti-clock-up" />
                </span>{" "}
                Not Marked
              </li>
            </ul>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Attendance Report - {selectedMonth}</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="d-flex flex-wrap me-2">
                  <input
                    type="month"
                    className="form-control mb-3"
                    value={monthDraft}
                    onChange={e => handleMonthChange(e.target.value)}
                  />
                </div>

                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(item => item._id === draftSystem)}
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={handleSystemChange}
                  />
                </div>

                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={classOptions}
                    value={classOptions.find(
                      opt => opt.value === draftClassId,
                    ) || null}
                    onChange={handleClassFilterChange}
                    placeholder={loadingClasses ? "Loading..." : "Select Class"}
                    isDisabled={loadingClasses}
                  />
                </div>

                {sectionOptions.length > 0 && (
                  <div className="mb-3 me-2">
                    <CommonSelect
                      className="select"
                      options={sectionOptions}
                      value={
                        sectionOptions.find(
                          opt => opt.value === draftSectionId,
                        ) || null
                      }
                      onChange={handleSectionFilterChange}
                      placeholder="All Sections"
                    />
                  </div>
                )}

                <div className="mb-3 me-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSearchClick}
                    disabled={loading}
                  >
                    <i className="ti ti-search me-1" />
                    Search
                  </button>
                </div>

                <div className="mb-3 me-2">
                  <button
                    type="button"
                    className="btn btn-outline-light bg-white"
                    onClick={handleResetFilters}
                    disabled={loading}
                  >
                    <i className="ti ti-refresh me-1" />
                    Reset
                  </button>
                </div>

                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" /> Sort by{" "}
                    {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {STUDENT_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                dataSource={tableData}
                columns={columns}
                Selection={false}
                loading={loading}
                setSearchText={setSearchText}
                hidePagination
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AttendanceReport
